import AutorenewIcon from "@mui/icons-material/Autorenew";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import StarIcon from "@mui/icons-material/Star";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import { useTranslation } from "react-i18next";
import {
  CameraIcon,
  DartIcon,
  GlobeIcon,
  KeyIcon,
  LineChartIcon,
  PenToolIcon,
  PhoneHeartIcon,
  RocketIcon,
  TimeAlarmIcon,
  VoiceRecordIcon
} from "../components/common/MuiSvgIcons/OtherIcons";
import HighlightedText from "../components/common/Translations/HighlightedText";
import { useTheme } from "@mui/material";

export const ONE_TIME_DISCOUNT_PER = 50;

export const WEBVIEW_APP_PURCHASE_PLAN_ALIAS_REV = {
  Solo_Monthly: "lite",
  Starter_Monthly: "premium",
  solo_monthly: "lite",
  starter_monthly: "premium",
  lite: "lite",
  premium: "premium",
  basic_monthly: "basic",
  Basic_Monthly: "basic",
  plus: "plus",
  edge: "edge",
  plus_replica: "plus_replica",
  core: "core",
  rise: "rise"
};

export const allPlanVars = {
  core: "core",
  rise: "rise",
  enterprise_25_plus: "enterprise_25_plus",
  sdk_plus: "predis_sdk_25_plus",
  sdk_wl_plus: "predis_sdk_25_wl_plus",
  plus: "plus",
  plus_replica: "plus_replica",
  edge: "edge",
  enterprise_25: "enterprise_25",
  enterprise_25_replica: "enterprise_25_replica",
  sdk: "predis_sdk_25",
  sdk_wl: "predis_sdk_25_wl",
  lite: "lite",
  basic: "basic",
  premium: "premium",
  agency_24: "agency_24",
  forever_free: "forever_free",
  forever_free_plus: "forever_free_plus",
  cbdemo_trial: "cbdemo_trial",
  popular_plans: ["rise"],
  sdk_plans: ["predis_sdk_25", "predis_sdk_25_wl", "predis_sdk_25_plus", "predis_sdk_25_wl_plus"],
  autoposting_excluded_plans: ["basic", "plus", "plus_replica", "core"],
  new_credit_plans: [
    "core",
    "rise",
    "enterprise_25_plus",
    "predis_sdk_25_plus",
    "predis_sdk_25_wl_plus"
  ],
  new_credit_plans_without_sdk: ["core", "rise", "enterprise_25_plus"],
  new_plans: ["plus", "edge", "enterprise_25", "predis_sdk_25", "predis_sdk_25_wl"],
  new_plans_without_sdk: ["plus", "edge", "enterprise_25"],
  free_plans: ["cbdemo_trial", "forever_free", "forever_free_plus"],
  old_plans: [
    "basic",
    "lite",
    "premium",
    "agency_24",
    "solo",
    "starter",
    "agency_23",
    "agency",
    "growth"
  ],
  legacy_plans: ["solo", "starter", "agency_23", "agency", "growth"], // need to define legacy_plans cause some addon conditions are different for them
  ltd_plans: ["as_59_ltd"],
  semrush_plans: ["semrush_base_plan"],
  semrush_base: "semrush_base_plan",
  add_on_keys: [
    "add_brand_only",
    "add_brand_accounts",
    "add_premium_assets",
    "add_extralimits",
    "add_extradownloads",
    "add_extracredits"
  ],
  addOns: {
    extraBrand: "add_brand_only",
    socialChannels: "add_brand_accounts",
    premiumAssets: "add_premium_assets",
    premiumAssetsSemrush: "add_premium_assets_semrush",
    extras: "add_extralimits",
    extraDownload: "add_extradownloads",
    extraCredits: "add_extracredits",
    extras_semrush: "add_extralimits_semrush",
    voiceover: "add_voiceover"
  },
  addOnsAdditional: {
    // keys should be same as values of addOns above
    add_brand_accounts: { option1: "add_premium_social_accounts" }
  },
  discount: {
    zero: 0,
    fifty: 50,
    seventeen: 16.6667
  }
};

export const allowedAddons = {
  [allPlanVars.core]: [
    allPlanVars.addOns.extraBrand,
    allPlanVars.addOns.socialChannels,
    allPlanVars.addOns.extraCredits
  ],
  [allPlanVars.rise]: [
    allPlanVars.addOns.extraBrand,
    allPlanVars.addOns.socialChannels,
    allPlanVars.addOns.extraCredits
  ],
  [allPlanVars.enterprise_25_plus]: [
    allPlanVars.addOns.socialChannels,
    allPlanVars.addOns.extraCredits
  ],
  [allPlanVars.sdk_plus]: [allPlanVars.addOns.socialChannels, allPlanVars.addOns.extraCredits],
  [allPlanVars.sdk_wl_plus]: [allPlanVars.addOns.socialChannels, allPlanVars.addOns.extraCredits]
};

export const paywallEntry = {
  socialPlatforms: "social_platforms",
  postLimit: "create_post",
  wordLimit: "idea_lab",
  downloadPremium: "download",
  usePremium: "use_premium",
  downloadFree: "download_free",
  publishFree: "publish_free",
  autopostingPremium: "autoposting_premium",
  brandLimit: "brand",
  voiceoverLimit: "voiceover",
  voiceoverPremium: "voiceover_premium",
  avatarPremium: "avatar_premium",
  others: "others",
  autopostingExcludedPlan: "autposting_excluded_plan",
  autopostingExpiredPlan: "autoposting_expired_plan",
  autopostingExhaustedPlan: "autoposting_exhausted_plan",
  autopostingExhaustedPostCredits: "autoposting_exhausted_post_credits"
};
export const pricingDetails = {
  [allPlanVars.forever_free]: {
    yearly: { price: 0, discount: 0 },
    monthly: { price: 0, discount: 0 }
  },
  [allPlanVars.forever_free_plus]: {
    yearly: { price: 0, discount: 0 },
    monthly: { price: 0, discount: 0 }
  },
  [allPlanVars.core]: { yearly: { price: 384, discount: 40 }, monthly: { price: 32, discount: 0 } },
  [allPlanVars.rise]: {
    yearly: { price: 948, discount: 50 },
    monthly: { price: 79, discount: 0 }
  },
  [allPlanVars.enterprise_25_plus]: {
    yearly: { price: 2988, discount: 15 },
    monthly: { price: 249, discount: 0 }
  },
  [allPlanVars.sdk_plus]: {
    yearly: { price: 5976, discount: 0 },
    monthly: { price: 498, discount: 0 }
  },
  [allPlanVars.sdk_wl_plus]: {
    yearly: { price: 11388, discount: 0 },
    monthly: { price: 949, discount: 0 }
  }
};
export const addOnPrices = {
  [allPlanVars.addOns.extraBrand]: {
    ...Object.fromEntries(
      [...allPlanVars.new_credit_plans, ...allPlanVars.new_plans, ...allPlanVars.old_plans].map(
        (plan) => [plan, { yearly: 60, monthly: 5, stackable: true }]
      )
    )
  },
  [allPlanVars.addOns.socialChannels]: {
    ...Object.fromEntries(
      [...allPlanVars.new_credit_plans, ...allPlanVars.new_plans, ...allPlanVars.old_plans].map(
        (plan) => [plan, { yearly: 60, monthly: 5, stackable: true, quantity: 1 }]
      )
    ),
    //Overriding addon Social Channels as per plan applicable
    //new credit plans
    enterprise_25_plus: { yearly: 1188, monthly: 99, stackable: true, quantity: 400 },
    rise: { yearly: 300, monthly: 25, stackable: true, quantity: 40 },
    predis_sdk_25_plus: { yearly: 1188, monthly: 99, stackable: true, quantity: 400 },
    predis_sdk_25_wl_plus: { yearly: 1188, monthly: 99, stackable: true, quantity: 400 },
    //new plans & old plans
    enterprise_25: { yearly: 1188, monthly: 99, stackable: true, quantity: 400 },
    edge: { yearly: 300, monthly: 25, stackable: true, quantity: 40 },
    predis_sdk_25: { yearly: 1188, monthly: 99, stackable: true, quantity: 400 },
    predis_sdk_25_wl: { yearly: 1188, monthly: 99, stackable: true, quantity: 400 },
    agency_24: { yearly: 1188, monthly: 99, stackable: true, quantity: 400 },
    premium: { yearly: 300, monthly: 25, stackable: true, quantity: 40 }
  },
  [allPlanVars.addOns.premiumAssets]: {
    ...Object.fromEntries(
      [...allPlanVars.old_plans].map((plan) => [plan, { yearly: 90, monthly: 9, stackable: false }])
    )
  },
  [allPlanVars.addOns.premiumAssetsSemrush]: {
    ...Object.fromEntries(
      [...allPlanVars.semrush_plans].map((plan) => [
        plan,
        { yearly: 90, monthly: 15, stackable: false }
      ])
    )
  },
  [allPlanVars.addOns.extras]: {
    ...Object.fromEntries(
      allPlanVars.old_plans.map((plan) => [plan, { yearly: 348, monthly: 29, stackable: true }])
    )
  },
  [allPlanVars.addOns.extraDownload]: {
    ...Object.fromEntries(
      allPlanVars.new_plans.map((plan) => [plan, { yearly: 348, monthly: 29, stackable: true }])
    )
  },
  [allPlanVars.addOns.extraCredits]: {
    ...Object.fromEntries(
      allPlanVars.new_credit_plans.map((plan) => [
        plan,
        { yearly: 348, monthly: 29, stackable: true }
      ])
    )
  },
  [allPlanVars.addOns.extras_semrush]: {
    ...Object.fromEntries(
      [...allPlanVars.semrush_plans].map((plan) => [
        plan,
        { yearly: 99.9, monthly: 20, stackable: false }
      ])
    )
  },
  [allPlanVars.addOns.voiceover]: {
    ...Object.fromEntries(
      [...allPlanVars.new_credit_plans, ...allPlanVars.new_plans, ...allPlanVars.old_plans].map(
        (plan) => [plan, { yearly: 99.9, monthly: 19.99, stackable: false }]
      )
    )
  },
  // additional varaints for addons
  [allPlanVars.addOnsAdditional.add_brand_accounts.option1]: {
    ...Object.fromEntries(
      [...allPlanVars.new_credit_plans, ...allPlanVars.new_plans, ...allPlanVars.old_plans].map(
        (plan) => [plan, { yearly: 250, monthly: 25, stackable: true }]
      )
    )
  }
};

export const additionalLimits = {
  [allPlanVars.forever_free]: {
    downloads: 8,
    voiceover: 5
  },
  [allPlanVars.plus]: {
    downloads: 20,
    voiceover: 10
  },
  [allPlanVars.edge]: {
    downloads: 80,
    voiceover: 20
  },
  [allPlanVars.enterprise_25]: {
    downloads: 260,
    voiceover: 40
  },
  [allPlanVars.sdk]: {
    downloads: 540
  },
  [allPlanVars.sdk_wl]: {
    downloads: 540
  },
  [allPlanVars.addOns.extraDownload]: {
    downloads: 20,
    voiceover: 10
  },
  [allPlanVars.addOns.extraCredits]: {
    credits: 1200
  }
};

export const useDownloadUpgrade = () => {
  const { t } = useTranslation();
  const getDownloadUpgrade = (currPlan, newPlan, currBillingCycle, newBillingCycle) => {
    const currPlanPrice = pricingDetails?.[currPlan]?.[currBillingCycle];
    const newPlanPrice = pricingDetails?.[newPlan]?.[newBillingCycle];

    const currFinalPrice =
      currPlanPrice?.price - (currPlanPrice?.price * currPlanPrice?.discount) / 100;

    const newFinalPrice =
      newPlanPrice?.price - (newPlanPrice?.price * newPlanPrice?.discount) / 100;

    if (isNaN(currFinalPrice) || newFinalPrice >= currFinalPrice) {
      return t("commonTexts.commonBtn.upgrade");
    } else {
      return t("commonTexts.commonBtn.downgrade");
    }
  };

  return { getDownloadUpgrade };
};

export const useAddOnContent = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const addOnContent = {
    [allPlanVars.addOns.premiumAssets]: {
      imgUrl: `${process.env.PUBLIC_URL}/premium_assets_paywall.jpg`,
      title: t("premium-asset-addon1"), //t("premium-asset-addon"),
      listTitle: t("why-upgrade"),
      listItems: [
        {
          icon: <RocketIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: t("unlimited-choice-premium-photos")
        },
        {
          icon: <CameraIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: t("professional-quality-that-sets-you-apart")
        },
        {
          icon: <TimeAlarmIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: t("save-time-with-quick-access")
        },
        {
          icon: <KeyIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: t("exclusive-content-for-premium")
        }
      ]
    },
    [allPlanVars.addOns.premiumAssetsSemrush]: {
      imgUrl: `${process.env.PUBLIC_URL}/premium_assets_paywall.jpg`,
      title: t("premium-asset-addon1"), // t("premium-asset-addon"),
      listTitle: t("paywall-features.subtitle"),
      listItems: [
        {
          icon: <RocketIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: t("unlimited-choice-premium-photos")
        },
        {
          icon: <CameraIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: t("professional-quality-that-sets-you-apart")
        },
        {
          icon: <TimeAlarmIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: t("save-time-with-quick-access")
        },
        {
          icon: <KeyIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: t("exclusive-content-for-premium")
        }
      ]
    },
    [allPlanVars.addOns.extras]: {
      imgUrl: `${process.env.PUBLIC_URL}/extras_paywall.jpg`,
      title: t("paywall-feature.title-extras"),
      listTitle: t("paywall-features.subtitle"),
      listItems: [
        {
          icon: <LineChartIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: <span>{t("paywall-features.more-posts")}</span>
        },
        {
          icon: <VoiceRecordIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: <span>{t("paywall-feature.voiceover")}</span>
        },
        {
          icon: <PenToolIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: <span>{t("paywall-feature.words")}</span>
        },
        {
          icon: <PenToolIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: <span>{t("paywall-feature.competitor-runs")}</span>
        }
      ]
    },
    [allPlanVars.addOns.extraDownload]: {
      imgUrl: `${process.env.PUBLIC_URL}/extras_paywall.jpg`,
      title: t("paywall-feature.extra-downloads.title"),
      listTitle: t("paywall-feature.extra-downloads.list-items"),
      listItems: [
        {
          icon: <LineChartIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: (
            <span>
              {t("paywall-feature.extra-downloads.benefit-1", {
                downloads: additionalLimits[allPlanVars.addOns.extraDownload]["downloads"]
              })}
              ,
            </span>
          )
        },
        {
          icon: <VoiceRecordIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: (
            <span>
              {t("paywall-feature.extra-downloads.benefit-2", {
                voiceovers: additionalLimits[allPlanVars.addOns.extraDownload]["voiceover"]
              })}
            </span>
          )
        }
      ]
    },
    [allPlanVars.addOns.extraCredits]: {
      imgUrl: `${process.env.PUBLIC_URL}/extra_credits_paywall.jpg`,
      title: t("paywall-feature.extra-credits.title"),
      listTitle: t("paywall-feature.extra-credits.list-items"),
      listItems: [
        {
          icon: <LineChartIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: (
            <HighlightedText
              i18nKey={t("paywall-feature.extra-credits.benefit-1")}
              highlights={{
                highlight1: (
                  <span style={{ color: theme.palette.primary.main }}>
                    {additionalLimits[allPlanVars.addOns.extraCredits].credits}
                  </span>
                )
              }}
            />
          )
        },
        {
          icon: <LineChartIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: <span>{t("paywall-feature.extra-credits.benefit-2")}</span>
        }
      ]
    },
    [allPlanVars.addOns.socialChannels]: {
      imgUrl: `${process.env.PUBLIC_URL}/socialChannel_paywall.jpg`,
      title: t("paywall-feature.title.social-channels"),
      listTitle: t("why-upgrade"),
      listItems: [
        {
          icon: <GlobeIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: t("paywall-feature.social-account1")
        },
        {
          icon: <DartIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: t("paywall-feature.social-account2")
        },
        {
          icon: <PhoneHeartIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: t("paywall-feature.social-account3")
        }
      ]
    },
    [allPlanVars.addOns.voiceover]: {
      imgUrl: `${process.env.PUBLIC_URL}/3685253.svg`,
      title: t("paywall-features.title.voiceover-limit-exhausted"),
      listTitle: t("paywall-features.subtitle"),
      listItems: [
        {
          icon: <StarIcon fontSize={"small"} color='warning' />,
          text: "Professional Quality: Ensure impeccable clarity, emotion, and expression from a pool of voice-over styles."
        },
        {
          icon: <LightbulbIcon fontSize={"small"} color='warning' />,
          text: "Versatility: Whether you need a soothing narration, an enthusiastic voiceover, or anything in between, our diverse pool of options can give your posts life."
        },
        {
          icon: <AutorenewIcon fontSize={"small"} color='primary' />,
          text: "Seamless Integration: Easily integrate our voice overs into your videos to create a polished and professional final product that resonates with your audience."
        },
        {
          icon: <WhatshotIcon fontSize={"small"} color='error' />,
          text: "Enhanced Engagement: Engage your viewers on a deeper level and keep them hooked from start to finish with captivating voice narration that enhances the impact of your message."
        }
      ]
    },
    [allPlanVars.addOns.extras_semrush]: {
      imgUrl: `${process.env.PUBLIC_URL}/extras_paywall.jpg`,
      title: "Unleash Infinite Creativity: Upgrade Now!",
      listTitle: t("paywall-features.subtitle"),
      listItems: [
        {
          icon: <LineChartIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: "More Posts: Generate more posts beyond the limit."
        },
        {
          icon: <PenToolIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: "Word Count Unleashed: Break free from word count restrictions."
        },
        {
          icon: <PenToolIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: "Add More Brands: Bring in extra brands to your collection."
        },
        {
          icon: <PenToolIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: "More social channels: Connect more social channels to your account."
        }
      ]
    },
    [allPlanVars.addOns.extraBrand]: {
      imgUrl: `${process.env.PUBLIC_URL}/socialChannel_paywall.jpg`,
      title: t("paywall-feature.extra-brand.title"),
      listTitle: t("why-upgrade"),
      listItems: [
        {
          icon: <GlobeIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: t("paywall-feature.extra-brand.subtitle-1")
        },
        {
          icon: <DartIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: t("paywall-feature.extra-brand.subtitle-2")
        },
        {
          icon: <PhoneHeartIcon fontSize={"small"} sx={{ fill: "transparent" }} />,
          text: t("paywall-feature.extra-brand.subtitle-3")
        }
      ]
    }
  };
  return addOnContent;
};

export const paddlePlanPriceIds =
  process.env.REACT_APP_PADDLE_ENV === "production"
    ? {
        [allPlanVars.plus]: {
          monthly: {
            price_id: "pri_01jz77jyvnrcez5m23ebk4an8n",
            one_time_discount_id: "dsc_01k2gxzpf842pbzap7xbgzwx08",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpq1jwwgsb97vdz1nzv1sd" },
              add_extradownloads: { price_id: "pri_01jz77ws91xxvt88emg72wmde6" },
              add_brand_accounts: { price_id: "pri_01jkdpv2hp1yn69r7ysdpkzwq9" } // single social channel
            }
          },
          yearly: {
            price_id: "pri_01jz77mctcsaj8e64k5b8gn0sv",
            discount_id: "dsc_01jkg4qa0jxcenc6b3fqsex235", // 40% discount
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpqpx23tm7z6qsx1t5fwyw" },
              add_extradownloads: { price_id: "pri_01jz77y0f488zqgrvy29xtsz0e" },
              add_brand_accounts: { price_id: "pri_01jkdpvrxadddez029ac1m2t0h" } // single social channel
            }
          }
        },
        [allPlanVars.edge]: {
          monthly: {
            price_id: "pri_01jz77nxnymd7ze9pbbvrsn8q6",
            one_time_discount_id: "dsc_01k2gxzpf842pbzap7xbgzwx08",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpq1jwwgsb97vdz1nzv1sd" },
              add_extradownloads: { price_id: "pri_01jz77ws91xxvt88emg72wmde6" },
              add_brand_accounts: { price_id: "pri_01jkg44bhd6w9cb62m41cq3sxk" } // 40 social channels
            }
          },
          yearly: {
            price_id: "pri_01jz77q260d39pcdxsm7h6q39h",
            discount_id: "dsc_01jkg4r485036t13c4z47q0925", // 50% discount
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpqpx23tm7z6qsx1t5fwyw" },
              add_extradownloads: { price_id: "pri_01jz77y0f488zqgrvy29xtsz0e" },
              add_brand_accounts: { price_id: "pri_01jkg454r34pk7kw79kjmycx3r" } // 40 social channels
            }
          }
        },
        [allPlanVars.enterprise_25]: {
          monthly: {
            price_id: "pri_01jz79f09gwr7z2kwywhf04xfz",
            one_time_discount_id: "dsc_01k2gxzpf842pbzap7xbgzwx08",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpq1jwwgsb97vdz1nzv1sd" },
              add_extradownloads: { price_id: "pri_01jz77ws91xxvt88emg72wmde6" },
              add_brand_accounts: { price_id: "pri_01jkg46kr6hnbdy38z7tqnqwqv" } // 400 social channels
            }
          },
          yearly: {
            price_id: "pri_01jz79g2x63asfshkme12xryz1",
            discount_id: "dsc_01jkg4s8c4zc421aj4zd9re6by", // 15% discount
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpqpx23tm7z6qsx1t5fwyw" },
              add_extradownloads: { price_id: "pri_01jz77y0f488zqgrvy29xtsz0e" },
              add_brand_accounts: { price_id: "pri_01jkg47wbqzw6fz9ht7dzx6dhh" } // 400 social channels
            }
          }
        },
        [allPlanVars.sdk]: {
          monthly: {
            price_id: "pri_01jz77zjjpm1f7dq1bctaqjyc0",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpq1jwwgsb97vdz1nzv1sd" },
              add_extradownloads: { price_id: "pri_01jz77ws91xxvt88emg72wmde6" },
              add_brand_accounts: { price_id: "pri_01jkg46kr6hnbdy38z7tqnqwqv" } // 400 social channels
            }
          },
          yearly: {
            price_id: "pri_01jz781w3nzj6n2jg1se1m27kz",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpqpx23tm7z6qsx1t5fwyw" },
              add_extradownloads: { price_id: "pri_01jz77y0f488zqgrvy29xtsz0e" },
              add_brand_accounts: { price_id: "pri_01jkg47wbqzw6fz9ht7dzx6dhh" } // 400 social channels
            }
          }
        },
        [allPlanVars.sdk_wl]: {
          monthly: {
            price_id: "pri_01jz783zjw4p5t27w2mkzj7vg5",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpq1jwwgsb97vdz1nzv1sd" },
              add_extradownloads: { price_id: "pri_01jz77ws91xxvt88emg72wmde6" },
              add_brand_accounts: { price_id: "pri_01jkg46kr6hnbdy38z7tqnqwqv" } // 400 social channels
            }
          },
          yearly: {
            price_id: "pri_01jz784x016aek41qkr9ech5m1",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpqpx23tm7z6qsx1t5fwyw" },
              add_extradownloads: { price_id: "pri_01jz77y0f488zqgrvy29xtsz0e" },
              add_brand_accounts: { price_id: "pri_01jkg47wbqzw6fz9ht7dzx6dhh" } // 400 social channels
            }
          }
        },
        //New Plans Credits
        [allPlanVars.core]: {
          monthly: {
            price_id: "pri_01k64nwq3ekqqtf85kfxc1sj6y",
            discount_id: null,
            one_time_discount_id: "dsc_01k2gxzpf842pbzap7xbgzwx08",
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpq1jwwgsb97vdz1nzv1sd" },
              add_extracredits: { price_id: "pri_01k64pc9qqvnqws7vs3rnvd22r" },
              add_brand_accounts: { price_id: "pri_01jkdpv2hp1yn69r7ysdpkzwq9" }
            }
          },
          yearly: {
            price_id: "pri_01k64nxt4waz1a6zhj6pav1qbx",
            discount_id: "dsc_01jkg4qa0jxcenc6b3fqsex235", // 40% discount
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpqpx23tm7z6qsx1t5fwyw" },
              add_extracredits: { price_id: "pri_01k64pebjhqv10x3dwrnfhxtyt" },
              add_brand_accounts: { price_id: "pri_01jkdpvrxadddez029ac1m2t0h" }
            }
          }
        },
        [allPlanVars.rise]: {
          monthly: {
            price_id: "pri_01k64nz9wrp1mxpms1t280cyvm",
            discount_id: null,
            one_time_discount_id: "dsc_01k2gxzpf842pbzap7xbgzwx08",
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpq1jwwgsb97vdz1nzv1sd" },
              add_extracredits: { price_id: "pri_01k64pc9qqvnqws7vs3rnvd22r" },
              add_brand_accounts: { price_id: "pri_01jkg44bhd6w9cb62m41cq3sxk" } // 40 social channels
            }
          },
          yearly: {
            price_id: "pri_01k64p00bj9ec39mr7hht3sjvf",
            discount_id: "dsc_01jkg4r485036t13c4z47q0925", // 50% discount
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpqpx23tm7z6qsx1t5fwyw" },
              add_extracredits: { price_id: "pri_01k64pebjhqv10x3dwrnfhxtyt" },
              add_brand_accounts: { price_id: "pri_01jkg454r34pk7kw79kjmycx3r" } // 40 social channels
            }
          }
        },
        [allPlanVars.enterprise_25_plus]: {
          monthly: {
            price_id: "pri_01k64p1xt0sfhp3d9cvbjw8wq4",
            discount_id: null,
            one_time_discount_id: "dsc_01k2gxzpf842pbzap7xbgzwx08",
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpq1jwwgsb97vdz1nzv1sd" },
              add_extracredits: { price_id: "pri_01k64pc9qqvnqws7vs3rnvd22r" },
              add_brand_accounts: { price_id: "pri_01jkg46kr6hnbdy38z7tqnqwqv" } // 400 social channels
            }
          },
          yearly: {
            price_id: "pri_01k64p2wc5ny06898m1er850hg",
            discount_id: "dsc_01jkg4s8c4zc421aj4zd9re6by", // 15% discount
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpqpx23tm7z6qsx1t5fwyw" },
              add_extracredits: { price_id: "pri_01k64pebjhqv10x3dwrnfhxtyt" },
              add_brand_accounts: { price_id: "pri_01jkg47wbqzw6fz9ht7dzx6dhh" } // 400 social channels
            }
          }
        },
        [allPlanVars.sdk_plus]: {
          monthly: {
            price_id: "pri_01k64p5cfgdm0d1sf00tqrjrwq",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpq1jwwgsb97vdz1nzv1sd" },
              add_extracredits: { price_id: "pri_01k64pc9qqvnqws7vs3rnvd22r" },
              add_brand_accounts: { price_id: "pri_01jkg46kr6hnbdy38z7tqnqwqv" } // 400 social channels
            }
          },
          yearly: {
            price_id: "pri_01k64p6nkrcchtczq3b5p1hm8c",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpqpx23tm7z6qsx1t5fwyw" },
              add_extracredits: { price_id: "pri_01k64pebjhqv10x3dwrnfhxtyt" },
              add_brand_accounts: { price_id: "pri_01jkg47wbqzw6fz9ht7dzx6dhh" } // 400 social channels
            }
          }
        },
        [allPlanVars.sdk_wl_plus]: {
          monthly: {
            price_id: "pri_01k64p9d04qt2ah0w4gnz5kd7h",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpq1jwwgsb97vdz1nzv1sd" },
              add_extracredits: { price_id: "pri_01k64pc9qqvnqws7vs3rnvd22r" },
              add_brand_accounts: { price_id: "pri_01jkg46kr6hnbdy38z7tqnqwqv" } // 400 social channels
            }
          },
          yearly: {
            price_id: "pri_01k64p9zeys91n5qx1hapekaw6",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jkdpqpx23tm7z6qsx1t5fwyw" },
              add_extracredits: { price_id: "pri_01k64pebjhqv10x3dwrnfhxtyt" },
              add_brand_accounts: { price_id: "pri_01jkg47wbqzw6fz9ht7dzx6dhh" } // 400 social channels
            }
          }
        }
      }
    : {
        //New Plans FUP
        [allPlanVars.plus]: {
          monthly: {
            price_id: "pri_01jydhq3tx12kjegzn2z59nkrw",
            discount_id: null,
            one_time_discount_id: "dsc_01k24tzv0mpm1ce412fg60pfar", //50% off
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw538b1g4nwnya3e6ggx64" },
              add_extradownloads: { price_id: "pri_01jydkpd1mnatezzjm40zzx8p8" },
              add_brand_accounts: { price_id: "pri_01jh84h967er5td8q3h3f6n30e" }
            }
          },
          yearly: {
            price_id: "pri_01jydhrc1rmgdeec3azw10h3t1",
            discount_id: "dsc_01jh82y15j5pj13xph9whvhjnj",
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw5hten7gyg39b6y3rqctz" },
              add_extradownloads: { price_id: "pri_01jydkqf4b6n5thb0p0jbbmbf8" },
              add_brand_accounts: { price_id: "pri_01jh84v2kb7egh117x1qtyy1b2" }
            }
          }
        },
        [allPlanVars.edge]: {
          monthly: {
            price_id: "pri_01jydhvww0bvdt5skmv2k9pccd",
            discount_id: null,
            one_time_discount_id: "dsc_01k24tzv0mpm1ce412fg60pfar",
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw538b1g4nwnya3e6ggx64" },
              add_extradownloads: { price_id: "pri_01jydkpd1mnatezzjm40zzx8p8" },
              add_brand_accounts: { price_id: "pri_01jhfvfd3ns4n5pw9ktzhhwefk" }
            }
          },
          yearly: {
            price_id: "pri_01jydhwx82yvgncfw1b6bd3y6g",
            discount_id: "dsc_01jh82yhhncs557hhr48kbjbnz",
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw5hten7gyg39b6y3rqctz" },
              add_extradownloads: { price_id: "pri_01jydkqf4b6n5thb0p0jbbmbf8" },
              add_brand_accounts: { price_id: "pri_01jhfvg5d7jns0wx0nea66v5wp" }
            }
          }
        },
        [allPlanVars.enterprise_25]: {
          monthly: {
            price_id: "pri_01jydk85ntfyb17xfjt3169se6",
            discount_id: null,
            one_time_discount_id: "dsc_01k24tzv0mpm1ce412fg60pfar",
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw538b1g4nwnya3e6ggx64" },
              add_extradownloads: { price_id: "pri_01jydkpd1mnatezzjm40zzx8p8" },
              add_brand_accounts: { price_id: "pri_01jhfw35rq8krd2mc6fxbhqvrc" }
            }
          },
          yearly: {
            price_id: "pri_01jydk9qnns0g2mh8g37xq4t5q",
            discount_id: "dsc_01jh82y15j5pj13xph9whvhjnj",
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw5hten7gyg39b6y3rqctz" },
              add_extradownloads: { price_id: "pri_01jydkqf4b6n5thb0p0jbbmbf8" },
              add_brand_accounts: { price_id: "pri_01jhfw3veqsc13ysewqtvxccap" }
            }
          }
        },
        [allPlanVars.sdk]: {
          monthly: {
            price_id: "pri_01jydj0g2dzfc4d7763qgr99me",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw538b1g4nwnya3e6ggx64" },
              add_extradownloads: { price_id: "pri_01jydkpd1mnatezzjm40zzx8p8" },
              add_brand_accounts: { price_id: "pri_01jhfw35rq8krd2mc6fxbhqvrc" }
            }
          },
          yearly: {
            price_id: "pri_01jydj1ppfqpd9a53a29b8awww",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw5hten7gyg39b6y3rqctz" },
              add_extradownloads: { price_id: "pri_01jydkqf4b6n5thb0p0jbbmbf8" },
              add_brand_accounts: { price_id: "pri_01jhfw3veqsc13ysewqtvxccap" }
            }
          }
        },
        [allPlanVars.sdk_wl]: {
          monthly: {
            price_id: "pri_01jyeej9bf59m2aptkk31vm42t",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw538b1g4nwnya3e6ggx64" },
              add_extradownloads: { price_id: "pri_01jydkpd1mnatezzjm40zzx8p8" },
              add_brand_accounts: { price_id: "pri_01jhfw35rq8krd2mc6fxbhqvrc" }
            }
          },
          yearly: {
            price_id: "pri_01jyeek6zydvmhy2w5h04x43gc",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw5hten7gyg39b6y3rqctz" },
              add_extradownloads: { price_id: "pri_01jydkqf4b6n5thb0p0jbbmbf8" },
              add_brand_accounts: { price_id: "pri_01jhfw3veqsc13ysewqtvxccap" }
            }
          }
        },
        //New Plans Credits
        [allPlanVars.core]: {
          monthly: {
            price_id: "pri_01k5gn527ecn34dmydthacs2wp",
            discount_id: null,
            one_time_discount_id: "dsc_01k24tzv0mpm1ce412fg60pfar",
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw538b1g4nwnya3e6ggx64" },
              add_extracredits: { price_id: "pri_01k5gnteaab1dvsda0kj4tw3sh" },
              add_brand_accounts: { price_id: "pri_01jh84h967er5td8q3h3f6n30e" }
            }
          },
          yearly: {
            price_id: "pri_01k5gn613dvd9taxsnh3kaeckw",
            discount_id: "dsc_01jh82y15j5pj13xph9whvhjnj",
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw5hten7gyg39b6y3rqctz" },
              add_extracredits: { price_id: "pri_01k5gnvhf1akzkbt2cshw9d3rq" },
              add_brand_accounts: { price_id: "pri_01jh84v2kb7egh117x1qtyy1b2" }
            }
          }
        },
        [allPlanVars.rise]: {
          monthly: {
            price_id: "pri_01k5gn8vepg2m9985zsmdnem31",
            discount_id: null,
            one_time_discount_id: "dsc_01k24tzv0mpm1ce412fg60pfar",
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw538b1g4nwnya3e6ggx64" },
              add_extracredits: { price_id: "pri_01k5gnteaab1dvsda0kj4tw3sh" },
              add_brand_accounts: { price_id: "pri_01jhfvfd3ns4n5pw9ktzhhwefk" }
            }
          },
          yearly: {
            price_id: "pri_01k5gn9hg5fp8j866sxzt7mmer",
            discount_id: "dsc_01jh82yhhncs557hhr48kbjbnz",
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw5hten7gyg39b6y3rqctz" },
              add_extracredits: { price_id: "pri_01k5gnvhf1akzkbt2cshw9d3rq" },
              add_brand_accounts: { price_id: "pri_01jhfvg5d7jns0wx0nea66v5wp" }
            }
          }
        },
        [allPlanVars.enterprise_25_plus]: {
          monthly: {
            price_id: "pri_01k5gnc99400kc5wgb6exn0bsj",
            discount_id: null,
            one_time_discount_id: "dsc_01k24tzv0mpm1ce412fg60pfar",
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw538b1g4nwnya3e6ggx64" },
              add_extracredits: { price_id: "pri_01k5gnteaab1dvsda0kj4tw3sh" },
              add_brand_accounts: { price_id: "pri_01jhfw35rq8krd2mc6fxbhqvrc" }
            }
          },
          yearly: {
            price_id: "pri_01k5gnd0t4se50mdz7adqhqtj5",
            discount_id: "dsc_01jh82y15j5pj13xph9whvhjnj",
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw5hten7gyg39b6y3rqctz" },
              add_extracredits: { price_id: "pri_01k5gnvhf1akzkbt2cshw9d3rq" },
              add_brand_accounts: { price_id: "pri_01jhfw3veqsc13ysewqtvxccap" }
            }
          }
        },
        [allPlanVars.sdk_plus]: {
          monthly: {
            price_id: "pri_01k5gngeg49sm4mp64qefhxb51",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw538b1g4nwnya3e6ggx64" },
              add_extracredits: { price_id: "pri_01k5gnteaab1dvsda0kj4tw3sh" },
              add_brand_accounts: { price_id: "pri_01jhfw35rq8krd2mc6fxbhqvrc" }
            }
          },
          yearly: {
            price_id: "pri_01k5gnh5nec6c6gaaanh7w8y82",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw5hten7gyg39b6y3rqctz" },
              add_extracredits: { price_id: "pri_01k5gnvhf1akzkbt2cshw9d3rq" },
              add_brand_accounts: { price_id: "pri_01jhfw3veqsc13ysewqtvxccap" }
            }
          }
        },
        [allPlanVars.sdk_wl_plus]: {
          monthly: {
            price_id: "pri_01k5gnkzq52fk9hzcq0a9jbe9b",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw538b1g4nwnya3e6ggx64" },
              add_extracredits: { price_id: "pri_01k5gnteaab1dvsda0kj4tw3sh" },
              add_brand_accounts: { price_id: "pri_01jhfw35rq8krd2mc6fxbhqvrc" }
            }
          },
          yearly: {
            price_id: "pri_01k5gnn6jm8zdvqsrmrwqmya69",
            discount_id: null,
            addOns: {
              add_brand_only: { price_id: "pri_01jhfw5hten7gyg39b6y3rqctz" },
              add_extracredits: { price_id: "pri_01k5gnvhf1akzkbt2cshw9d3rq" },
              add_brand_accounts: { price_id: "pri_01jhfw3veqsc13ysewqtvxccap" }
            }
          }
        }
      };

export const paddleProductIdMap =
  process.env.REACT_APP_PADDLE_ENV === "production"
    ? {
        //new FUP Plans
        pro_01jz77gg2s957bp92s6mve7k67: { is_base_plan: true, plan_id: allPlanVars.plus },
        pro_01jz77mxe8kaggnt2c4ejppgce: { is_base_plan: true, plan_id: allPlanVars.edge },
        pro_01jz79ebk2np455rvdf2r3tm3x: { is_base_plan: true, plan_id: allPlanVars.enterprise_25 },
        pro_01jz77ysspbnvhxpf1nb7t254m: { is_base_plan: true, plan_id: allPlanVars.sdk },
        pro_01jz782rm0h40ws9wh6zqfxgw5: { is_base_plan: true, plan_id: allPlanVars.sdk_wl },
        //new Credit Plans
        pro_01k64nv8ygxtzm3xsb0hf2he7t: { is_base_plan: true, plan_id: allPlanVars.core },
        pro_01k64nykezx1n0y7ye6gq8gvx3: { is_base_plan: true, plan_id: allPlanVars.rise },
        pro_01k64p0vjkz7dva9xbv4gts0bb: {
          is_base_plan: true,
          plan_id: allPlanVars.enterprise_25_plus
        },
        pro_01k64p44hm0mqebf8tr92y3eyp: { is_base_plan: true, plan_id: allPlanVars.sdk_plus },
        pro_01k64p7ahcymmdh01hcp2ste8j: { is_base_plan: true, plan_id: allPlanVars.sdk_wl_plus },
        // make sure add-on plan ids match with those in paddlePlanPriceIds and allPlanVars
        pro_01jkdph2e7v8g09p3nbvngzm8d: {
          is_base_plan: false,
          plan_id: "add_brand_only",
          stackable: true,
          qty_multiple: 1,
          valid_on_plans: [...allPlanVars.new_plans, ...allPlanVars.new_credit_plans]
        },
        pro_01jz77vs6peet4zfm6rkq56tfw: {
          is_base_plan: false,
          plan_id: "add_extradownloads",
          stackable: true,
          qty_multiple: 1,
          valid_on_plans: [...allPlanVars.new_plans]
        },
        pro_01k64papfx4rrh7ffhnf3vrfy8: {
          is_base_plan: false,
          plan_id: "add_extracredits",
          stackable: true,
          qty_multiple: 1,
          valid_on_plans: [...allPlanVars.new_credit_plans]
        },
        pro_01jkdpjszf9v9xffkc6rbmkq0b: {
          is_base_plan: false,
          plan_id: "add_brand_accounts",
          stackable: true,
          qty_multiple: 1,
          valid_on_plans: [allPlanVars.plus, allPlanVars.core]
        },
        pro_01jkdpkcccg7crqkvzc4kctrat: {
          is_base_plan: false,
          plan_id: "add_brand_accounts",
          stackable: true,
          qty_multiple: 40,
          valid_on_plans: [allPlanVars.edge, allPlanVars.rise]
        }, // 40 social channels
        pro_01jkdpkzjcr6rca3n56an91wnw: {
          is_base_plan: false,
          plan_id: "add_brand_accounts",
          stackable: true,
          qty_multiple: 400,
          valid_on_plans: [
            allPlanVars.enterprise_25,
            allPlanVars.enterprise_25_plus,
            ...allPlanVars.sdk_plans
          ]
        } // 400 social channels
      }
    : {
        //new FUP Plans
        pro_01jydhmk1wgg0cgq5t728srgx9: { is_base_plan: true, plan_id: allPlanVars.plus },
        pro_01jydhv8rmaqez7ry69yy7c1zk: { is_base_plan: true, plan_id: allPlanVars.edge },
        pro_01jydk6dcg96w6rqv1kppvc6ew: { is_base_plan: true, plan_id: allPlanVars.enterprise_25 },
        pro_01jydhyqv4w7s30xsc2qme8cd1: { is_base_plan: true, plan_id: allPlanVars.sdk },
        pro_01jyeeh6zr2s59xb2j6y4ye6hq: { is_base_plan: true, plan_id: allPlanVars.sdk_wl },
        //new Credit Plans
        pro_01k5gn4575wtbhjt3tvnk59ts9: { is_base_plan: true, plan_id: allPlanVars.core },
        pro_01k5gn85ty3tx9k1epwkc7vccw: { is_base_plan: true, plan_id: allPlanVars.rise },
        pro_01k5gnbfvddv29vd3wn35y6ne7: {
          is_base_plan: true,
          plan_id: allPlanVars.enterprise_25_plus
        },
        pro_01k5gnfm076npqzmav4qezx6sa: { is_base_plan: true, plan_id: allPlanVars.sdk_plus },
        pro_01k5gnk989h5agfdt8y9f7c5pa: { is_base_plan: true, plan_id: allPlanVars.sdk_wl_plus },
        // make sure add-on plan ids match with those in paddlePlanPriceIds and allPlanVars
        pro_01jhfw4bvjnf7te186s2cd1rs4: {
          is_base_plan: false,
          plan_id: "add_brand_only",
          stackable: true,
          qty_multiple: 1,
          valid_on_plans: [...allPlanVars.new_plans, ...allPlanVars.new_credit_plans]
        },
        pro_01jydknd7e3x42ywggt5qj41hc: {
          is_base_plan: false,
          plan_id: "add_extradownloads",
          stackable: true,
          qty_multiple: 1,
          valid_on_plans: [...allPlanVars.new_plans]
        },
        pro_01k5gnsjayksbtc8rgrr2ee4a1: {
          is_base_plan: false,
          plan_id: "add_extracredits",
          stackable: true,
          qty_multiple: 1,
          valid_on_plans: [...allPlanVars.new_credit_plans]
        },
        pro_01jh84fxywz9dyeptr6b0xhbbt: {
          is_base_plan: false,
          plan_id: "add_brand_accounts",
          stackable: true,
          qty_multiple: 1,
          valid_on_plans: [allPlanVars.plus, allPlanVars.core]
        },
        pro_01jhfvejz7aa963c08a3s80y9y: {
          is_base_plan: false,
          plan_id: "add_brand_accounts",
          stackable: true,
          qty_multiple: 40,
          valid_on_plans: [allPlanVars.edge, allPlanVars.rise]
        }, // 40 social channels
        pro_01jhfw2g2swpg1q8sv0wxmspd2: {
          is_base_plan: false,
          plan_id: "add_brand_accounts",
          stackable: true,
          qty_multiple: 400,
          valid_on_plans: [
            allPlanVars.enterprise_25,
            allPlanVars.enterprise_25_plus,
            ...allPlanVars.sdk_plans
          ]
        } // 400 social channels
      };

export const addOnChannelIds = {
  [allPlanVars.basic]: "add_brand_accounts",
  [allPlanVars.plus]: "add_brand_accounts",
  [allPlanVars.edge]: "add_premium_social_accounts",
  [allPlanVars.enterprise_25]: "add_agency_social_accounts",
  [allPlanVars.sdk]: "add_agency_social_accounts",
  [allPlanVars.sdk_wl]: "add_agency_social_accounts",
  [allPlanVars.core]: "add_brand_accounts",
  [allPlanVars.rise]: "add_premium_social_accounts",
  [allPlanVars.enterprise_25_plus]: "add_agency_social_accounts",
  [allPlanVars.sdk_plus]: "add_agency_social_accounts",
  [allPlanVars.sdk_wl_plus]: "add_agency_social_accounts"
};

export const planOrder = {
  [allPlanVars.forever_free]: 0,
  [allPlanVars.plus]: 1,
  [allPlanVars.edge]: 2,
  [allPlanVars.enterprise_25]: 3,
  [allPlanVars.sdk]: 3,
  [allPlanVars.sdk_wl]: 3,
  //new credits plans
  [allPlanVars.forever_free_plus]: 0,
  [allPlanVars.core]: 1,
  [allPlanVars.rise]: 2,
  [allPlanVars.enterprise_25_plus]: 3,
  [allPlanVars.sdk_plus]: 3,
  [allPlanVars.sdk_wl_plus]: 3
};

export const planCredits = {
  [allPlanVars.forever_free_plus]: 500,
  [allPlanVars.core]: 1300,
  [allPlanVars.rise]: 3200,
  [allPlanVars.enterprise_25_plus]: 10000,
  [allPlanVars.sdk_plus]: 24000,
  [allPlanVars.sdk_wl_plus]: 46000
};

export const creditsUsage = {
  //Normal Gen
  single_image: 20,
  carousel: 120,
  video: 15,
  per_minute_video: 18,
  voiceover_video: 15,
  per_minute_voiceover_video: 24,

  //Ecom Gen
  ecom_single_image: 25,
  ecom_carousel: 150,
  ecom_video: 40,
  per_minute_ecom_video: 78,
  ecom_voiceover_video: 45,
  per_minute_ecom_voiceover_video: 84,

  //UGC
  ugc_video: 35,
  per_minute_ugc_video: 60,
  ecom_ugc_video: 70,
  per_minute_ecom_ugc_video: 126,

  //AI Gen
  per_minute_ai_gen_stock_video_wan: 60,
  per_minute_ai_gen_stock_video_seedream: 96,

  //4.0
  predis_4_0_image: 20,
  ecom_predis_4_0_image: 25,

  //product photoshoot"
  product_photoshoot: 25,

  //editing
  editing_single_image: 5,
  editing_ecom_single_image: 5,
  editing_per_minute_voiceover: 30,
  editing_per_minute_ugc: 36,

  //regenration
  per_minute_voice_over: 10,
  per_image_regeneration: 5,
  per_resize_generation: 10
};

export const useCreditUsage = () => {
  const { t } = useTranslation();

  const joinWithSlash = (...parts) => {
    return parts.filter(Boolean).join(" / ");
  };

  const creditUsage = [
    {
      title: `${t("commonTexts.image")} & ${t(
        "contentLibrary.newCreateFlow.contentType.adPost.title"
      )}`,
      values: [
        {
          type: joinWithSlash(
            t("contentLibrary.newCreateFlow.contentType.adPost.title"),
            t("credit-usage.social-image-post")
          ),
          credits: joinWithSlash(
            t("checklist.stepDetails.creditReward", { count: creditsUsage.single_image }),
            t("commonTexts.image").toLowerCase()
          )
        },
        {
          type: joinWithSlash(
            t("credit-usage.ecom-product-ad-creative"),
            t("credit-usage.product-post")
          ),
          credits: joinWithSlash(
            t("checklist.stepDetails.creditReward", { count: creditsUsage.ecom_single_image }),
            t("commonTexts.image").toLowerCase()
          )
        }
      ]
    },
    {
      title: t("commonTexts.videos"),
      values: [
        {
          type: t("credit-usage.standard-video"),
          credits: joinWithSlash(
            t("checklist.stepDetails.creditReward", { count: creditsUsage.per_minute_video / 2 }),
            t("commonTexts.video").toLowerCase()
          )
        },
        {
          type: t("credit-usage.video-with-voiceoever"),
          credits: joinWithSlash(
            t("checklist.stepDetails.creditReward", {
              count: creditsUsage.per_minute_voiceover_video / 6
            }),
            `10${t("commonTexts.seconds")}`
          )
        },
        {
          type: t("credit-usage.video-with-ugc-avatar"),
          credits: joinWithSlash(
            t("checklist.stepDetails.creditReward", {
              count: creditsUsage.per_minute_ugc_video / 6
            }),
            `10${t("commonTexts.seconds")}`
          )
        }
      ]
    },
    {
      title: t("credit-usage.ecom-product-videos"),
      values: [
        {
          type: t("credit-usage.standard-product-video"),
          credits: joinWithSlash(
            t("checklist.stepDetails.creditReward", {
              count: creditsUsage.per_minute_ecom_video / 2
            }),
            t("commonTexts.video").toLowerCase()
          )
        },
        {
          type: t("credit-usage.product-video-with-voiceover"),
          credits: joinWithSlash(
            t("checklist.stepDetails.creditReward", {
              count: creditsUsage.per_minute_ecom_voiceover_video / 6
            }),
            `10${t("commonTexts.seconds")}`
          )
        },
        {
          type: t("credit-usage.product-video-with-ugc-avatar"),
          credits: joinWithSlash(
            t("checklist.stepDetails.creditReward", {
              count: creditsUsage.per_minute_ecom_ugc_video / 6
            }),
            `10${t("commonTexts.seconds")}`
          )
        }
      ]
    },
    {
      title: t("credit-usage.ai-generated-video-models"),
      values: [
        {
          type: "WAN 2.2",
          credits: joinWithSlash(
            t("checklist.stepDetails.creditReward", {
              count: creditsUsage.per_minute_ai_gen_stock_video_wan / 6
            }),
            `10${t("commonTexts.seconds")}`
          )
        },
        {
          type: "Seedance-1-lite",
          credits: joinWithSlash(
            t("checklist.stepDetails.creditReward", {
              count: creditsUsage.per_minute_ai_gen_stock_video_seedream / 6
            }),
            `10${t("commonTexts.seconds")}`
          )
        }
      ]
    },
    {
      title: t("credit-usage.other-content-types"),
      values: [
        {
          type: t("commonTexts.carousel"),
          credits: t("credit-usage.as-per-slides")
        },
        {
          type: t("create-post.product-photo-shoot.title"),
          credits: joinWithSlash(
            t("checklist.stepDetails.creditReward", {
              count: creditsUsage.product_photoshoot
            }),
            t("commonTexts.image").toLowerCase()
          )
        }
      ]
    },
    {
      title: t("credit-usage.editing"),
      values: [
        {
          type: joinWithSlash(
            t("contentLibrary.newCreateFlow.contentType.adPost.title"),
            t("credit-usage.social-image-post"),
            t("credit-usage.carousel-slide")
          ),
          credits: joinWithSlash(
            t("checklist.stepDetails.creditReward", {
              count: creditsUsage.editing_single_image
            }),
            t("commonTexts.image").toLowerCase()
          )
        },
        {
          type: joinWithSlash(
            t("credit-usage.ecom-product-ad-creative"),
            t("credit-usage.product-post")
          ),
          credits: joinWithSlash(
            t("checklist.stepDetails.creditReward", {
              count: creditsUsage.editing_ecom_single_image
            }),
            t("commonTexts.image").toLowerCase()
          )
        },
        {
          type: t("contentLibrary.editModal.creativeTab.voiceoverTab.voiceover"),
          credits: joinWithSlash(
            t("checklist.stepDetails.creditReward", {
              count: creditsUsage.editing_per_minute_voiceover / 6
            }),
            `10${t("commonTexts.seconds")}`
          )
        },
        {
          type: t("credit-usage.ugc"),
          credits: joinWithSlash(
            t("checklist.stepDetails.creditReward", {
              count: creditsUsage.editing_per_minute_ugc / 6
            }),
            `10${t("commonTexts.seconds")}`
          )
        }
      ]
    }
  ];

  const columns = [
    { field: "type", headerName: t("commonTexts.type") },
    { field: "credits", headerName: t("plan-noti.user-plan.credits") }
  ];

  return { columns, creditUsage };
};
